/**
 * SC-1323 §4.2: the normalized track PlayerShell plays. A page builds one or two of these; the
 * shell knows nothing about where the content comes from.
 *
 * @typedef {{ atMs: number, durationMs: number }} TrackInsertion
 * @typedef {{ id?: string, title: string, start: number, image?: string|null }} TrackChapter
 * @typedef {{ id: string, title: string, url: string, button: string, image: string, start: number, end: number, till: string|null }} TrackCta
 * @typedef {{ lang: string, label: string, url: string }} TrackTranscript
 * @typedef {{ hls?: { masterUrl: string, duration: number }, audio?: { url: string, duration: number } }} TrackMedia
 * @typedef {{
 *   id: string,
 *   itemId: string,
 *   kind: "video"|"audio",
 *   title: string,
 *   artworkUrl: string|null,
 *   media: TrackMedia,
 *   chapters: TrackChapter[],
 *   ctas: TrackCta[],
 *   transcripts: TrackTranscript[],
 *   timeline: { insertions: TrackInsertion[] },
 * }} Track
 *
 * chapters and ctas are on the track's OWN playback timeline. timeline.insertions are the DC
 * insertions of this track's media relative to the original timeline: the shell shifts the
 * transcript cues (published on the original timeline) by them, and a sameTimeline pair maps
 * positions through the audio track's insertions.
 */

/** @param {Track} track */
export function trackDuration(track) {
    return track?.media?.hls?.duration ?? track?.media?.audio?.duration ?? 0
}

/** @param {Track} track */
export function wireInsertions(track) {
    return (track?.timeline?.insertions ?? []).map((insertion) => ({ at_ms: insertion.atMs, duration_ms: insertion.durationMs }))
}

const isString = (value) => typeof value === "string"
const isSeconds = (value) => typeof value === "number" && Number.isFinite(value) && value >= 0
const CTA_STRINGS = ["id", "title", "url", "button", "image"]

function validCta(cta) {
    return CTA_STRINGS.every((key) => isString(cta?.[key])) && isSeconds(cta.start) && isSeconds(cta.end) && cta.end > cta.start && (cta.till === null || isString(cta.till))
}

/**
 * The Track contract, checked in dev builds (warnOnInvalidTracks). Returns the problems, [] when valid.
 */
export function validateTracks(tracks) {
    if (!Array.isArray(tracks) || tracks.length > 2) {
        return ["tracks must be an array of at most two tracks"]
    }

    const errors = []
    const kinds = new Set()

    tracks.forEach((track, index) => {
        const at = `tracks[${index}]`

        if (!track || typeof track !== "object") {
            errors.push(`${at} must be an object`)

            return
        }

        if (!isString(track.id) || track.id === "") {
            errors.push(`${at}.id must be a non-empty string`)
        }
        if (!isString(track.itemId) || track.itemId === "") {
            errors.push(`${at}.itemId must be a non-empty string`)
        }
        if (track.kind !== "video" && track.kind !== "audio") {
            errors.push(`${at}.kind must be video or audio`)
        } else if (kinds.has(track.kind)) {
            errors.push(`${at}.kind ${track.kind} appears twice`)
        } else {
            kinds.add(track.kind)
        }
        if (!isString(track.title)) {
            errors.push(`${at}.title must be a string`)
        }
        if (track.artworkUrl !== null && !isString(track.artworkUrl)) {
            errors.push(`${at}.artworkUrl must be a string or null`)
        }
        if (track.kind === "video" && !(isString(track.media?.hls?.masterUrl) && isSeconds(track.media?.hls?.duration))) {
            errors.push(`${at}.media.hls needs masterUrl and duration`)
        }
        if (track.kind === "audio" && !(isString(track.media?.audio?.url) && isSeconds(track.media?.audio?.duration))) {
            errors.push(`${at}.media.audio needs url and duration`)
        }
        if (!Array.isArray(track.chapters) || !track.chapters.every((chapter) => isString(chapter?.title) && isSeconds(chapter?.start))) {
            errors.push(`${at}.chapters must be [{title, start}]`)
        }
        if (!Array.isArray(track.ctas) || !track.ctas.every(validCta)) {
            errors.push(`${at}.ctas must be [{id, title, url, button, image, start, end, till}]`)
        }
        if (!Array.isArray(track.transcripts) || !track.transcripts.every((transcript) => isString(transcript?.lang) && isString(transcript?.label) && isString(transcript?.url))) {
            errors.push(`${at}.transcripts must be [{lang, label, url}]`)
        }
        if (!Array.isArray(track.timeline?.insertions) || !track.timeline.insertions.every((insertion) => isSeconds(insertion?.atMs) && isSeconds(insertion?.durationMs))) {
            errors.push(`${at}.timeline.insertions must be [{atMs, durationMs}]`)
        }
    })

    return errors
}

export function warnOnInvalidTracks(tracks, { dev = Boolean(import.meta.env?.DEV) } = {}) {
    if (!dev) {
        return
    }

    for (const error of validateTracks(tracks)) {
        console.warn(`[PlayerShell] ${error}`)
    }
}
